import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminSignup() {
  const { user, checking, signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF4311]" /></div>;
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup(name, email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-signup-page" className="min-h-screen flex items-center justify-center px-4">
      <div className="jpd-card w-full max-w-md p-8 sm:p-10">
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10  bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </span>
          <div>
            <p className="font-display font-bold leading-tight">BRAD'S <span className="text-[#FF4311]">DETAILING</span></p>
            <p className="text-xs text-slate-500">Owner Login</p>
          </div>
        </div>

        <h1 className="mt-7 text-xl sm:text-2xl font-semibold flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#FF4311]" /> Owner Login
        </h1>
        <p className="mt-2 text-sm text-slate-400">Sign up for dashboard access — you'll land in the dashboard right after.</p>

        <form data-testid="admin-signup-form" onSubmit={submit} className="mt-6 space-y-4">
          <input
            data-testid="admin-signup-name-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            className="jpd-input"
            autoComplete="name"
          />
          <input
            data-testid="admin-signup-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="jpd-input"
            autoComplete="email"
          />
          <input
            data-testid="admin-signup-password-input"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (8+ characters)"
            className="jpd-input"
            autoComplete="new-password"
          />
          {error && (
            <p data-testid="admin-signup-error" className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30  px-4 py-2.5">
              {error}
            </p>
          )}
          <button
            data-testid="admin-signup-button"
            disabled={loading}
            className="w-full bg-primary hover:bg-[#C20A0B] disabled:opacity-60 text-white font-semibold py-3.5  transition-colors inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Creating account…" : "Sign Up"}
          </button>
        </form>

        <div className="mt-5 flex items-center justify-between text-sm">
          <Link to="/admin/login" data-testid="admin-signup-login-link" className="text-[#FF4311] hover:underline">
            Already have an account? Sign in
          </Link>
          <Link to="/" data-testid="admin-signup-home-link" className="text-slate-400 hover:text-white transition-colors">
            Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
